import LinkOffIcon from "@mui/icons-material/LinkOff";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { CircularProgress, Divider, Paper, Tooltip, Typography } from "@mui/material";
import { Person as PersonType } from "../../api";
import { formatRelativeTime } from "../../utils/date-formatting";
import { getDisplayName } from "../../utils/privacy";
import { useGlobalState } from "../globalState";
import { LocationDataTooltip } from "../location/LocationDataTooltip";
import FormattedSummary from "./person-formatted-summary";
import { PersonLinks } from "./person-links";
import { ProfileHeader } from "./profile-header";

export const PersonSummary = ({
	personInfo,
	showFormattedSummary = false,
	onNameClick,
}: {
	personInfo: PersonType;
	showFormattedSummary?: boolean;
	/** When provided, the name renders as a button that fires this callback
	 * (e.g. opens a PersonModal) instead of navigating to /person/:id. */
	onNameClick?: () => void;
}) => {
	const { currentUser } = useGlobalState();

	const displayName = personInfo
		? getDisplayName(personInfo.name, personInfo.id, currentUser?.userprofile)
		: "";

	return (
		<>
			{personInfo !== null ? (
				<Paper data-component="person-summary-paper">
					<ProfileHeader
						name={displayName}
						onNameClick={onNameClick}
						href={onNameClick ? undefined : `/person/${personInfo.id}`}
						headlineHtml={personInfo.headline}
						afterName={
							<>
								{currentUser.is_superuser &&
									personInfo.last_extracted && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ ml: 1, verticalAlign: "middle" }}
											data-component="profile-age-inline"
										>
											Profile age:{" "}
											{formatRelativeTime(
												Date.parse(personInfo.last_extracted)
											)}
										</Typography>
									)}
								{personInfo.unavailable ? (
									<Tooltip
										title="LinkedIn profile deleted"
										data-component="profile-deleted-tooltip"
									>
										<LinkOffIcon
											fontSize="small"
											color="error"
											sx={{ ml: 0.5, verticalAlign: "middle" }}
											data-component="profile-deleted-icon"
										/>
									</Tooltip>
								) : null}
								{personInfo.open_to_work ? (
									<Tooltip
										title="Open to Work"
										data-component="open-to-work-tooltip"
									>
										<WorkOutlineIcon
											fontSize="small"
											color="success"
											sx={{ ml: 0.5, verticalAlign: "middle" }}
											data-component="open-to-work-icon"
										/>
									</Tooltip>
								) : null}
								<PersonLinks href={personInfo.url} />
							</>
						}
						location={
							<LocationDataTooltip
								location_map_id={personInfo.location_id}
								ostmd_id={personInfo.ostmd_id}
								locationText={personInfo.country_region}
								entityId={personInfo.id}
								entityType="Person"
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", mt: 0.5 }}
									data-component="person-location"
								>
									{personInfo.country_region}
								</Typography>
							</LocationDataTooltip>
						}
					/>
					{showFormattedSummary ? (
						<>
							<Divider sx={{ mx: 2 }} data-component="summary-divider" />
							<FormattedSummary
								standalone={false}
								personInfo={personInfo}
							/>
						</>
					) : null}
				</Paper>
			) : (
				<CircularProgress data-component="person-loading-spinner" />
			)}
		</>
	);
};
